// @flow
import React, { Component } from 'react';
import AnimatingProgressBar from 'app/common/react/components/growth/unauth/signup/GoogleOneTap/AnimatingProgressBar';
import i18n from 'app/common/lib/i18n';
import Image from 'app/common/react/components/growth/unauth/Image/Image';
import loginUtil from 'app/www-unauth/lib/loginUtil';
import logStats from 'app/common/lib/logStats';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import ResponseTransitionState from 'app/common/react/components/growth/unauth/signup/FullPageResponse/ResponseTransitionModal';
import type { Pin as PinFlowType } from 'app/packages/pinterest-flow-types/pin';
import { getPinImage } from 'app/common/react/components/growth/unauth/pin/pinUtils';
import { Box, Text, Avatar, TextArea, Button, Letterbox, Heading } from 'gestalt';

const styles = {
  responseBox: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    position: 'relative',
    textAlign: 'center',
    margin: 'auto',
    minHeight: '450px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.45)',
    display: 'inline-block',
  },
  respondState: {
    maxWidth: '60.5em',
    maxHeight: '500px',
  },
  transitionState: {
    maxWidth: '50.5em',
    maxHeight: '700px',
    height: '640px',
    width: '50.5em',
  },
  pinImageStyle: {
    display: 'inline-block',
    position: 'relative',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  pinImage: {
    width: '262px',
    height: '362px',
    objectFit: 'cover',
  },
  placeholderText: {
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    bottom: '0px',
    color: '#f7f7f7',
    fontSize: '12px',
    height: '56px',
    left: '0px',
    lineHeight: '150%',
    marginTop: '-6px',
    opacity: '0.5',
    padding: '0 14px',
    position: 'absolute',
    right: '0px',
    textAlign: 'center',
    textShadow: '0 -1px rgba(0,0,0,0.17)',
    top: '45%',
    wordWrap: 'break-word',
  },
};

type Props = {|
  sender: {
    id: string,
    first_name: string,
    image_medium_url?: string,
  },
  pin: PinFlowType,
  isResponseSignup: boolean,
  responseType: string,
|};

type State = {|
  response: string,
  responseBoxState: 'respond' | 'transition' | 'complete',
|};

const SETHEIGHT = 362;
const SETWIDTH = 262;
const IMAGESIZE = 'orig';

const renderImagePlaceholder = (text, style) => (
  <div style={style}>
    <h4 style={styles.placeholderText}>{text}</h4>
  </div>
);

class FullPageResponse extends Component<Props, State> {
  state = {
    response: '',
    responseBoxState: 'respond',
  };

  handleResponseChange = ({ value }: { value: string }) => {
    this.setState({ response: value });
  };

  handleSendMessage = () => {
    const { sender, isResponseSignup, pin, responseType } = this.props;
    const { response } = this.state;
    logStats.increment(`web.social.unauth.response.message.${responseType}.click`, 1);

    const sendMessageResource = ResourceFactory.create('ConversationsResource', {
      user_ids: [sender.id],
      text: response,
      pin: pin.id,
    });
    sendMessageResource.callCreate().then((response) => {
      if (isResponseSignup) {
        logStats.increment(`web.social.unauth.response.message.signup.${responseType}.send`, 1);
        this.setState({ responseBoxState: 'transition' });
      } else {
        logStats.increment(`web.social.unauth.response.message.login.${responseType}.send`, 1);
        loginUtil.handleRedirect('/notifications/?tab=inbox');
      }
    });
  };
  handleSkipMessage = () => {
    const { isResponseSignup, responseType } = this.props;

    if (isResponseSignup) {
      logStats.increment(`web.social.unauth.response.signup.${responseType}.skip`, 1);
      loginUtil.handleSignupRedirect('/', true);
    } else {
      logStats.increment(`web.social.unauth.response.login.${responseType}.skip`, 1);
      loginUtil.handleRedirect('/notifications/?tab=inbox');
    }
  };
  handleSkipMessage = () => {
    const { isResponseSignup, responseType } = this.props;

    if (isResponseSignup) {
      logStats.increment(`web.social.unauth.response.signup.${responseType}.skip`, 1);
      loginUtil.handleSignupRedirect('/', true);
    } else {
      logStats.increment(`web.social.unauth.response.login.${responseType}.skip`, 1);
      loginUtil.handleRedirect('/notifications/?tab=inbox');
    }
  };
  renderModalHeader() {
    const { sender } = this.props;
    const firstName = sender ? sender.first_name : 'no first name';
    const image = sender ? sender.image_medium_url : '';

    const headerText = i18n._(
      'So, what do you think?',
      'asking reciever what they think of the pin sent to them',
    );
    return (
      <Box
        alignItems="center"
        justifyContent="between"
        direction="row"
        display="flex"
        flex="grow"
        marginBottom={3}
      >
        <Box marginBottom={2}>
          <Heading size="md">{headerText}</Heading>
        </Box>
        <Box>
          <Avatar name={firstName} size="lg" src={image} />
        </Box>
      </Box>
    );
  }

  renderResponseArea() {
    const { sender } = this.props;
    const { response } = this.state;
    const firstName = sender ? sender.first_name : '';
    const placeHolderText = firstName
      ? i18n
          ._('Reply to {{ name }}', 'placholder text prompting users to reply back to sender(name)')
          .replace('{{ name }}', firstName)
      : '';

    const sendButtonText = i18n._('Send message', 'button text to send a message');
    const continueButtonText = i18n._(
      'Skip for now',
      'button text to continue signup flow without sending a message',
    );
    const isResponseNotEmpty = response.trim().length > 0;
    return (
      <Box marginTop={6}>
        <Box marginBottom={6}>
          <TextArea
            id="unauth_response"
            onChange={this.handleResponseChange}
            value={response}
            placeholder={placeHolderText}
            rows={6}
          />
        </Box>
        <Box marginBottom={1}>
          <Button
            text={sendButtonText}
            color={isResponseNotEmpty ? 'red' : 'gray'}
            disabled={!isResponseNotEmpty}
            accessibilityLabel={i18n._('Send message', ' accessibilty label for sending a message')}
            onClick={this.handleSendMessage}
          />
        </Box>
        <Box>
          <Button
            text={continueButtonText}
            color="white"
            accessibilityLabel={i18n._(
              'Skip for now',
              ' accessibilty label for continuing signup flow without a response message',
            )}
            onClick={this.handleSkipMessage}
          />
        </Box>
      </Box>
    );
  }

  renderImage = (description: string, imgStyle: Object, srcUrl: string, image: Object) => {
    const imageComponent = (
      <Image
        alt={description}
        lazyLoad={false}
        renderPlaceholder={renderImagePlaceholder(description, imgStyle)}
        src={srcUrl}
        style={imgStyle}
      />
    );

    if (SETWIDTH !== undefined && SETHEIGHT !== undefined) {
      const aspectRatio = image.width / image.height;
      return (
        <Letterbox contentAspectRatio={aspectRatio} height={SETHEIGHT} width={SETWIDTH}>
          {imageComponent}
        </Letterbox>
      );
    } else {
      return imageComponent;
    }
  };

  renderPin = (closeupPin: PinFlowType) => {
    const image = getPinImage(closeupPin, IMAGESIZE);
    const srcUrl = image.url;
    const text = closeupPin.title || closeupPin.description;
    return (
      <Box shape="rounded" minWidth={262}>
        <div style={styles.pinImageStyle}>
          {this.renderImage(closeupPin.description, styles.pinImage, srcUrl, image)}
        </div>
        <Box marginTop={2}>
          <Text truncate weight="bold">
            {text}
          </Text>
        </Box>
      </Box>
    );
  };
  renderResponseState = () => {
    const { pin } = this.props;

    return (
      <Box
        direction="row"
        alignItems="start"
        justifyContent="between"
        display="flex"
        flex="grow"
        padding={8}
      >
        <Box width="55%">
          {this.renderModalHeader()}
          {this.renderResponseArea()}
        </Box>
        <Box>{this.renderPin(pin)}</Box>
      </Box>
    );
  };

  render() {
    const { sender } = this.props;
    const { responseBoxState } = this.state;
    const boxState = responseBoxState === 'respond' ? styles.respondState : styles.transitionState;
    const responseBoxStyle = {
      transition: 'all .25s cubic-bezier(0.66, 0.025, 0.33, 0.975)',
      ...styles.responseBox,
      ...boxState,
    };
    return (
      <div data-test-id="signup" style={responseBoxStyle}>
        {responseBoxState === 'respond' ? (
          this.renderResponseState()
        ) : (
          <Box>
            <AnimatingProgressBar
              duration={6500}
              finalProgress={100}
              showLightGrayBackground
              rounded
            />
            <ResponseTransitionState sender={sender} />
          </Box>
        )}
      </div>
    );
  }
}

export default FullPageResponse;
